import { useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { SEVERITY_NOTIFICATION } from '@/common/constants';
import { AppEvent, emitter } from '@/plugin/mitt';
import authService from '@/services/authService';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchAIUsageRequest } from '@/store/slices/aiUsageSlice';
import { getCurrentUserSuccess } from '@/store/slices/authSlice';
import { useSubscription } from '@/hooks/useSubscription';

/** Minimum time between plan re-checks triggered by focusing the app. */
const RECHECK_INTERVAL_MS = 60 * 1000;
/** setTimeout cannot wait longer than ~24.8 days; later expiries are caught by focus re-checks. */
const MAX_TIMER_MS = 2 ** 31 - 1;

/**
 * Keeps the learner's plan fresh: re-checks it when the app regains focus and at the moment it
 * expires (so AI features lock without a reload), and warns once a day before it expires.
 */
const SubscriptionWatcher: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const userId = useAppSelector((state) => state.auth.user?.id);
    const { subscription, isExpiringSoon, expiresAtText } = useSubscription();
    const lastCheckRef = useRef(Date.now());

    const recheck = useCallback(async () => {
        if (!authService.hasSession()) {
            return;
        }
        lastCheckRef.current = Date.now();
        try {
            // Quiet refresh: no loading state, and a failed request leaves the session as it is.
            const user = await authService.getCurrentUser();
            dispatch(getCurrentUserSuccess(user));
            dispatch(fetchAIUsageRequest());
        } catch {
            // Ignore; the next focus or the server's own limit check will catch up.
        }
    }, [dispatch]);

    useEffect(() => {
        const onFocus = () => {
            if (document.visibilityState === 'visible' && Date.now() - lastCheckRef.current > RECHECK_INTERVAL_MS) {
                recheck();
            }
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
    }, [recheck]);

    useEffect(() => {
        if (!subscription) {
            return undefined;
        }
        const msUntilExpiry = dayjs(subscription.expires_at).diff(dayjs()) + 1000;
        if (msUntilExpiry <= 0 || msUntilExpiry > MAX_TIMER_MS) {
            return undefined;
        }
        const timer = window.setTimeout(recheck, msUntilExpiry);
        return () => window.clearTimeout(timer);
    }, [subscription, recheck]);

    useEffect(() => {
        if (!subscription || !isExpiringSoon || !userId) {
            return;
        }
        const key = `subscription-warning:${userId}:${subscription.code}:${dayjs().format('YYYY-MM-DD')}`;
        try {
            if (localStorage.getItem(key)) {
                return;
            }
            localStorage.setItem(key, '1');
        } catch {
            // Storage unavailable: still warn, it may just repeat on reload.
        }
        emitter.emit(AppEvent.SHOW_NOTIFICATION, {
            message: t('common.subscription.expiringSoon', { plan: subscription.name, date: expiresAtText }),
            severity: SEVERITY_NOTIFICATION.WARNING,
            duration: 8000,
        });
    }, [subscription, isExpiringSoon, expiresAtText, userId, t]);

    return null;
};

export default SubscriptionWatcher;
